import { z } from 'zod';
import { MOTIFS_ECART, MOTIF_LIBRE_MAX, motifEcartSchema } from './ecart.js';

/** Type PostgreSQL `statut_candidature`. */
export const STATUTS_CANDIDATURE = [
  'nouveau',
  'a_contacter',
  'contacte',
  'rdv_planifie',
  'entretien_realise',
  'rapport_remis',
  'justificatifs_recus',
  'embauche',
  'ecarte',
  'desistement',
  'vivier',
] as const;
export const statutCandidatureSchema = z.enum(STATUTS_CANDIDATURE);
export type StatutCandidature = z.infer<typeof statutCandidatureSchema>;

export const LIBELLES_STATUT: Record<StatutCandidature, string> = {
  nouveau: 'Nouveau',
  a_contacter: 'À contacter',
  contacte: 'Contacté',
  rdv_planifie: 'Rendez-vous planifié',
  entretien_realise: 'Entretien réalisé',
  rapport_remis: 'Rapport remis',
  justificatifs_recus: 'Justificatifs reçus',
  embauche: 'Embauché',
  ecarte: 'Écarté',
  desistement: 'Désistement',
  vivier: 'Vivier',
};

/** Sorties possibles à tout moment (docs/MODELE_DONNEES.md § Statuts). */
export const STATUTS_SORTIE = ['ecarte', 'desistement', 'vivier'] as const;

/** Chaîne nominale ; toute étape en cours peut aussi basculer vers une sortie. */
const SUITE: Record<StatutCandidature, readonly StatutCandidature[]> = {
  nouveau: ['a_contacter'],
  a_contacter: ['contacte'],
  contacte: ['rdv_planifie'],
  rdv_planifie: ['entretien_realise'],
  entretien_realise: ['rapport_remis'],
  rapport_remis: ['justificatifs_recus'],
  justificatifs_recus: ['embauche'],
  embauche: [],
  ecarte: [],
  desistement: [],
  vivier: ['a_contacter'],
};

/**
 * `embauche` est terminal : le dossier vit désormais dans la fiche employé.
 * Un dossier écarté ou désisté ne se rouvre que par le vivier, jamais en
 * reprenant l'étape où il s'était arrêté.
 */
export function transitionsPossibles(
  depuis: StatutCandidature,
): readonly StatutCandidature[] {
  if (depuis === 'embauche') return [];
  if (depuis === 'ecarte' || depuis === 'desistement') return ['vivier'];
  if (depuis === 'vivier') return SUITE.vivier;
  return [...SUITE[depuis], ...STATUTS_SORTIE];
}

/** Écarter un dossier se motive toujours ; les autres sorties peuvent l'être. */
export function motifObligatoire(statut: StatutCandidature): boolean {
  return statut === 'ecarte';
}

/**
 * Changement de statut manuel (PATCH /candidatures/:id). Pour un écart, les
 * cases cochées arrivent dans `motifs` et le texte libre dans `motif` ; l'API
 * compose elle-même le `motif_sortie` lisible (`composerMotifEcart`), pour que
 * la fiche, le comparateur et les exports disent tous la même chose.
 */
export const changementStatutSchema = z
  .object({
    statut: statutCandidatureSchema,
    motif: z.string().max(500).nullable().optional(),
    motifs: z.array(motifEcartSchema).max(MOTIFS_ECART.length).optional(),
  })
  .strict();
export type ChangementStatut = z.infer<typeof changementStatutSchema>;

/**
 * Pastille posée à la main depuis la fiche ou la liste, sans passer par un
 * appel (demande du 2026-09-19) : PUT /candidatures/:id/potentiel. `null`
 * efface la pastille. Le prochain appel clôturé avec une pastille la remplace,
 * comme avant ; un appel plus ancien ne l'écrase plus (`potentiel_manuel_le`).
 */
/**
 * Étiquette libre posée à côté de la pastille (demande du 2026-09-19) :
 * « comptabilité », « vente », « caissière »… Elle dit le profil en un mot,
 * là où le chiffre dit ce qu'on en pense. Portée par la candidature, comme la
 * pastille. Ce n'est pas un référentiel : le recruteur écrit ce qu'il veut,
 * mais jamais d'âge, de sexe, de situation familiale ni de santé
 * (CLAUDE.md § Interdits) — l'interface le rappelle.
 */
export const ETIQUETTE_MAX = 40;

export const etiquetteCandidatureSchema = z
  .object({
    etiquette: z
      .string()
      .trim()
      .max(ETIQUETTE_MAX)
      .nullable()
      // Un texte vide efface l'étiquette, comme `null`.
      .transform((valeur) => (valeur ? valeur : null)),
  })
  .strict();
export type EtiquetteCandidature = z.infer<typeof etiquetteCandidatureSchema>;

/**
 * Petit mot devant le nom (demande du 2026-09-19), **distinct de l'étiquette
 * de profil** : l'étiquette dit le métier (« comptabilité »), ce mémo dit ce
 * qu'on veut retenir du dossier (« à rappeler lundi », « recommandée par le
 * chef d'atelier »). Porté par la candidature, modifiable là où il s'affiche.
 * Mêmes interdits que partout : ni âge, ni sexe, ni situation familiale, ni
 * santé (CLAUDE.md § Interdits).
 */
export const MEMO_MAX = 60;

export const memoCandidatureSchema = z
  .object({
    memo: z
      .string()
      .trim()
      .max(MEMO_MAX)
      .nullable()
      // Un texte vide efface le mémo, comme `null`.
      .transform((valeur) => (valeur ? valeur : null)),
  })
  .strict();
export type MemoCandidature = z.infer<typeof memoCandidatureSchema>;

export const pastilleManuelleSchema = z
  .object({
    potentiel: z.number().int().min(0).max(5).nullable(),
    motif: z.string().max(MOTIF_LIBRE_MAX).nullable().optional(),
  })
  .strict();
export type PastilleManuelle = z.infer<typeof pastilleManuelleSchema>;

/** Sources de **message** : ce que le worker sait reconnaître dans la boîte. */
export const SOURCES = [
  'tanitjobs',
  'tunisietravail',
  'keejob',
  'direct',
  'transfert',
] as const;
export const sourceSchema = z.enum(SOURCES);
export type Source = z.infer<typeof sourceSchema>;

/**
 * Tout ce que `candidatures.source` peut porter. Aux sources de message
 * s'ajoutent les candidatures qui ne viennent d'aucun message : reprises du
 * vivier, reçues par le formulaire public, ou saisies après un échange
 * WhatsApp (docs/FORMULAIRE_PUBLIC.md). La colonne est un `text` libre en
 * base : cette liste est la seule énumération qui fasse foi.
 */
export const SOURCES_CANDIDATURE = [...SOURCES, 'vivier', 'formulaire', 'whatsapp'] as const;
export const sourceCandidatureSchema = z.enum(SOURCES_CANDIDATURE);
export type SourceCandidature = z.infer<typeof sourceCandidatureSchema>;

/**
 * Pastille de potentiel (docs/BAREME.md § 1). La couleur ne va jamais seule :
 * l'interface affiche toujours le chiffre, et le libellé au survol.
 */
export interface NiveauPastille {
  valeur: number | null;
  couleur: string;
  libelle: string;
}

export const PASTILLE: readonly NiveauPastille[] = [
  { valeur: null, couleur: '#9ca3af', libelle: 'Pas encore appelé' },
  { valeur: 0, couleur: '#dc2626', libelle: 'À écarter' },
  { valeur: 1, couleur: '#ea580c', libelle: 'Hors sujet' },
  { valeur: 2, couleur: '#ca8a04', libelle: 'À revoir faute de mieux' },
  { valeur: 3, couleur: '#2563eb', libelle: 'Recevable, à faire venir' },
  { valeur: 4, couleur: '#65a30d', libelle: 'Bon profil' },
  { valeur: 5, couleur: '#16a34a', libelle: 'Prioritaire' },
];

export function pastille(valeur: number | null | undefined): NiveauPastille {
  const trouve = PASTILLE.find((n) => n.valeur === (valeur ?? null));
  return trouve ?? PASTILLE[0]!;
}

/**
 * POST /candidatures/:id/deplacer — la candidature CHANGE d'annonce ; elle
 * n'est pas recopiée (ça, c'est le rattachement depuis le vivier, qui garde
 * l'ancienne et en crée une neuve). Tout la suit : contacts, évaluation,
 * documents, chartes. L'adéquation se recalcule face aux critères de la
 * nouvelle annonce.
 */
export const deplacementCandidatureSchema = z.object({ annonceId: z.string().uuid() }).strict();
export type DeplacementCandidature = z.infer<typeof deplacementCandidatureSchema>;

/**
 * Numéro ajouté à la main sur une fiche (demande du 2026-09-21) :
 * POST /candidatures/:id/telephones, et `…/telephones/retirer` pour le
 * reprendre. Le candidat rappelle d'un autre poste, laisse le numéro d'un
 * proche, corrige le sien : jusqu'ici seuls les CV alimentaient les autres
 * numéros, et l'équipe n'avait nulle part où écrire celui-là.
 *
 * Ce numéro **ne rapproche jamais deux fiches** : le numéro principal reste la
 * seule clé de dédoublonnage. Il est normalisé comme un numéro joignable
 * (tunisien canonique, ou étranger nettoyé), le contrôle de forme est fait par
 * le service : le schéma ne garde que la saisie brute, pour que le message de
 * refus puisse parler du numéro tel qu'il a été tapé.
 */
export const TELEPHONES_AJOUTES_MAX = 5;

export const numeroCandidatureSchema = z
  .object({ telephone: z.string().trim().min(1).max(30) })
  .strict();
export type NumeroCandidature = z.infer<typeof numeroCandidatureSchema>;
